import type { Column } from '@tanstack/react-table';
import { PlusCircleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../badge';
import { Button } from '../button';
import { Input } from '../../forms/input';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { Separator } from '../separator';

export type DateRange = { from?: string; to?: string };

function formatRange({ from, to }: DateRange) {
  if (from && to) {
    return `${from} → ${to}`;
  }
  return from ? `≥ ${from}` : `≤ ${to}`;
}

interface DataTableDateFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title: string;
}

export function DataTableDateFilter<TData, TValue>({
  column,
  title,
}: DataTableDateFilterProps<TData, TValue>) {
  const { t } = useTranslation();
  const range = (column?.getFilterValue() as DateRange | undefined) ?? {};
  const hasValue = Boolean(range.from || range.to);

  const setBound = (bound: 'from' | 'to', value: string) => {
    const next = { ...range, [bound]: value || undefined };
    column?.setFilterValue(!next.from && !next.to ? undefined : next);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 border-dashed">
          <PlusCircleIcon className="mr-2 size-4" />
          {title}
          {hasValue && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                {formatRange(range)}
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-2 p-3" align="start">
        <label className="flex items-center justify-between gap-2 text-sm">
          <span className="text-muted-foreground">{t('dataTable.from')}</span>
          <Input
            type="date"
            className="w-40"
            value={range.from ?? ''}
            onChange={event => setBound('from', event.target.value)}
          />
        </label>
        <label className="flex items-center justify-between gap-2 text-sm">
          <span className="text-muted-foreground">{t('dataTable.to')}</span>
          <Input
            type="date"
            className="w-40"
            value={range.to ?? ''}
            onChange={event => setBound('to', event.target.value)}
          />
        </label>
        {hasValue && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => column?.setFilterValue(undefined)}
          >
            {t('dataTable.clearFilters')}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}
